import type React from "react";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/**
 * Catches uncaught render errors so a single broken page degrades to a readable
 * message + reload instead of a completely blank white screen. Also surfaces the
 * error text so it can be reported (and the root cause pinpointed) rather than
 * silently swallowed in the console.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("RenderError:", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div style={styles.wrap} role="alert">
          <div style={styles.card}>
            <div className="eyebrow" style={{ marginBottom: "0.6rem" }}>Page error</div>
            <h1 style={styles.title}>Something went wrong on this page.</h1>
            <p style={styles.copy}>
              Reloading usually clears it. If it keeps happening, copy the detail below and send it over.
            </p>
            <pre style={styles.detail}>{this.state.error.message || String(this.state.error)}</pre>
            <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const styles: Record<string, React.CSSProperties> = {
  wrap: { display: "flex", justifyContent: "center", padding: "3rem 1.25rem" },
  card: {
    width: "min(34rem, 100%)",
    background: "var(--ink-2)",
    border: "1px solid var(--rule)",
    borderRadius: "var(--r-lg, 10px)",
    padding: "1.75rem",
  },
  title: { margin: "0 0 0.5rem", fontSize: "1.25rem", fontWeight: 800, color: "var(--text-strong)" },
  copy: { margin: "0 0 1rem", color: "var(--text-secondary)", fontSize: "0.92rem", lineHeight: 1.5 },
  detail: {
    margin: "0 0 1.25rem",
    padding: "0.75rem",
    background: "var(--ink-1, #0f1217)",
    border: "1px solid var(--rule)",
    borderRadius: "var(--r-md, 6px)",
    color: "var(--text-default)",
    fontSize: "0.82rem",
    fontFamily: "var(--font-body)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    maxHeight: "12rem",
    overflow: "auto",
  },
};
